import React, { useEffect } from 'react';
import PropTypes, { object } from 'prop-types';
import { get } from 'lodash';
import cx from 'classnames';
import { Button, Icon, Position, Tooltip } from '@chaser/components';
import useOrganisation from '../../hooks/useOrganisation';
import pluralize from 'pluralize';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import format from 'date-fns/format';
import style from './SyncButton.module.scss';
import OrganisationService from '../../services/organisation-service';
import useShortCode from '../../hooks/useShortCode';
import useFetch from '../../hooks/useFetch';
import { hasOrganisationSynced } from '../../util/organisation';

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const SyncButton = ({ onSyncStart = () => {}, onSyncFinish = () => {}, syncAction }) => {
  const { execute, loading: syncing, error, reset, success } = syncAction;
  const org = useOrganisation(null, { suspense: false });
  const lastSynced = org?.lastRefreshDate;
  const hasSynced = hasOrganisationSynced(org);
  const expiredToken = get(org, 'accessToken.expired', false);
  const shortCode = useShortCode();
  const { data, refetch } = useFetch(OrganisationService.getUpdating, shortCode, {
    throwOnError: false,
  });
  const isUpdating = data || syncing;
  const isCSVImport = org?.accountingSoftware === 'csv' && org?.accessToken?.type === 'manual';

  let text = 'Sync now';

  if (isCSVImport) {
    text = 'Update';
  }

  let icon = <Icon name="sync" size={24} />;
  if (expiredToken) {
    text = 'Disconnected';
  } else if (isUpdating) {
    if (isCSVImport) {
      text = 'Updating...';
    } else {
      text = 'Syncing...';
    }
  } else if (error) {
    text = 'Error!';
    icon = <Icon name="alert-square" size={20} />;
  } else if (success) {
    text = 'Complete';
    icon = <Icon name="check" size={24} />;
  }

  useEffect(() => {
    // Keep polling while a manual sync is in flight or the server still reports
    // updating, so we do not miss organisation.updating after syncing flips false.
    if (hasSynced && !data && !syncing) {
      return undefined;
    }

    const interval = setInterval(() => {
      refetch();
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSynced, data, syncing]);

  async function sync() {
    if (expiredToken) {
      return;
    }

    onSyncStart();
    await Promise.all([execute(org.ShortCode), wait(1500)]);
    onSyncFinish();

    await wait(3000);
    reset();
  }

  function getDays(date) {
    return Math.abs(differenceInCalendarDays(new Date(date), new Date()));
  }

  function getTime(date) {
    return format(date, 'HH:mm');
  }

  const lastRefreshDays = lastSynced && getDays(lastSynced);
  const lastSyncTime = lastSynced ? getTime(new Date(lastSynced)) : '';

  return (
    <Tooltip
      content={
        hasSynced
          ? `Last synced: ${
              lastRefreshDays === 0
                ? `Today at ${lastSyncTime}`
                : `${pluralize('day', lastRefreshDays, true)} ago`
            }`
          : 'Last sync date not available'
      }
      position={Position.BOTTOM}
    >
      <Button
        data-testid="sync-button"
        data-trackingid="sync-button"
        disabled={
          Boolean(isUpdating) || Boolean(success) || Boolean(error) || Boolean(expiredToken)
        }
        icon={icon}
        className={cx(isUpdating && style.syncing)}
        onClick={sync}
        appearance="minimal"
        intent="inverted"
      >
        {text}
      </Button>
    </Tooltip>
  );
};

export default SyncButton;

SyncButton.propTypes = {
  onSyncStart: PropTypes.func,
  onSyncFinish: PropTypes.func,
  syncAction: object.isRequired,
};
